import { useMemo, useState, useEffect } from "react";

import {
  Modal,
  Frame,
  TitleBar,
  List,
  DataTable,
  CustomDropdown,
  Checkbox,
  Input,
  Button,
} from "@react95/core";

import { Regedit, Regedit205, Regedit206, FilePick, Mailnews8 } from "@react95/icons";

import { useApp } from "../../context/AppContext";
import { useAgent } from "../../context/ClippyContext";

import { WINDOW_NAMES } from "../../config/const";
import { getModalSizeProps } from "../../config/window";

export const RegistryEditorDef = {
  id: WINDOW_NAMES.RegistryEditor,
  title: "Registry Editor",
  icon: <Regedit variant="16x16_4" />,
  hasButton: true,
};

export default function RegistryEditor() {
  const { closeModal, sessionId, resetSession, setTheme, theme, sendMessage } = useApp();
  const { showAgent, currentAgent } = useAgent();
  const icon = RegistryEditorDef.icon;
  const sizeProps = useMemo(
    () => getModalSizeProps("350px", "550px", "normal", "260px", "300px"),
    [],
  );

  // initial entries generator (keeps `sessionId`, `theme`, and `currentAgent` up to date)
  const makeInitialEntries = (
    session: string | null,
    currentTheme: string,
    agent: string | null,
  ) => [
    {
      id: "reg-1",
      key: "ThemeSelect",
      type: "theme",
      value: currentTheme || "win95",
      icon: <Regedit206 variant="16x16_4" />,
    },
    {
      id: "reg-0",
      key: "SessionId",
      type: "readonly",
      value: session || "N/A",
      icon: <Regedit206 variant="16x16_4" />,
    },
    {
      id: "reg-2",
      key: "AgentName",
      type: "agent",
      value: agent || "Clippy",
      icon: <Regedit205 variant="16x16_4" />,
    },

    {
      id: "reg-3",
      key: "AudioVolume",
      type: "number",
      value: 0,
      icon: <Regedit205 variant="16x16_4" />,
    },
    {
      id: "reg-4",
      key: "NoClip",
      type: "boolean",
      value: 0,
      icon: <Regedit205 variant="16x16_4" />,
    },
    {
      id: "reg-5",
      key: "DevMode",
      type: "boolean",
      value: 0,
      icon: <Regedit205 variant="16x16_4" />,
    },
  ];

  const [entries, setEntries] = useState(() =>
    makeInitialEntries(sessionId, theme, currentAgent),
  );

  // keep session id, theme, and current agent up to date
  useEffect(() => {
    setEntries((prev) =>
      prev.map((e) => (e.key === "SessionId" ? { ...e, value: sessionId || "N/A" } : e)),
    );
  }, [sessionId]);

  useEffect(() => {
    setEntries((prev) =>
      prev.map((e) => (e.key === "ThemeSelect" ? { ...e, value: theme || "win95" } : e)),
    );
  }, [theme]);

  useEffect(() => {
    setEntries((prev) =>
      prev.map((e) => (e.key === "AgentName" ? { ...e, value: currentAgent || "Clippy" } : e)),
    );
  }, [currentAgent]);

  // ui helper data
  // available themes (win95 is at the top and selects the base/default theme)
  const THEMES = [
    "win95",
    "millenium",
    "storm",
    "marine",
    "spruce",
    "plum",
    "rainyDay",
    "slate",
    "travel",
    "maple",
    "rose",
  ].map((t) => ({
    value: t,
    label: t.charAt(0).toUpperCase() + t.slice(1),
  }));

  const AGENT_OPTIONS = [
    "Bonzi",
    "Clippy",
    "F1",
    "Genie",
    "Genius",
    "Links",
    "Merlin",
    "Peedy",
    "Rocky",
    "Rover",
  ].map((a) => ({ value: a, label: a }));

  // simple entry management helpers
  const updateEntryValue = (id: string, newValue: any) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, value: newValue } : e)));
  };

  const renderValueControl = (entry: any) => {
    switch (entry.type) {
      case "theme":
        return (
          <CustomDropdown
            options={THEMES}
            value={entry.value}
            onChange={(value: string) => {
              updateEntryValue(entry.id, value);
              try {
                setTheme(value);
              } catch (err) {
                console.error("failed to set theme:", err);
              }
            }}
          />
        );

      case "agent":
        return (
          <CustomDropdown
            options={AGENT_OPTIONS}
            value={entry.value}
            onChange={(value: string) => {
              updateEntryValue(entry.id, value);

              // notify backend character service about the change
              try {
                sendMessage?.("character", "set_character", { id: value });
              } catch (err) {
                console.error("failed to send set_character message", err);
              }

              // update client agent immediately for snappy UX
              try {
                showAgent?.(value);
              } catch (err) {
                console.error("failed to show agent locally", err);
              }
            }}
          />
        );

      case "boolean":
        return (
          <Checkbox
            checked={Boolean(entry.value)}
            onChange={(e: any) => updateEntryValue(entry.id, e.target.checked ? 1 : 0)}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={String(entry.value)}
            onChange={(e: any) => updateEntryValue(entry.id, Number(e.target.value))}
            style={{ width: "80px" }}
          />
        );

      case "readonly":
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                paddingTop: "6px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {String(entry.value)}
            </div>

            <Button
              onClick={() => {
                // confirm before resetting session
                // eslint-disable-next-line no-restricted-globals
                const ok = confirm(
                  "Reset session? This will clear saved session ids and request a new session.",
                );
                if (!ok) return;

                try {
                  resetSession();
                } catch (err) {
                  console.error("failed to reset session:", err);
                }
              }}
            >
              Reset Session
            </Button>
          </div>
        );

      default:
        return (
          <Input
            value={String(entry.value)}
            onChange={(e: any) => updateEntryValue(entry.id, e.target.value)}
            style={{ width: "220px" }}
          />
        );
    }
  };

  const handleCloseModal = () => {
    closeModal(RegistryEditorDef.id);
  };

  const columns = [
    { key: "name", label: "Name", width: "35%" },
    { key: "data", label: "Data", width: "65%" },
  ];

  const data = entries.map((entry) => ({
    name: (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          minWidth: 0,
          flex: 1,
          // overflow: "hidden",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            flex: "0 0 auto",
            marginRight: "6px",
          }}
        >
          {entry.icon}
        </span>

        <span
          title={entry.key}
          style={{
            display: "block",
            // overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: "bold",
            minWidth: 0,
            flex: 1,
          }}
        >
          {entry.key}
        </span>
      </div>
    ),
    data: (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{ flex: 1 }}>{renderValueControl(entry)}</div>
      </div>
    ),
  }));

  return (
    <Modal
      key={RegistryEditorDef.id}
      id={RegistryEditorDef.id}
      icon={icon}
      title={RegistryEditorDef.title}
      dragOptions={sizeProps.dragOptions}
      titleBarOptions={[
        <Modal.Minimize key="minimize" />,
        <TitleBar.Close key="close" onClick={() => closeModal(RegistryEditorDef.id)} />,
      ]}
      defaultClosed={true}
      height={sizeProps.height}
      width={sizeProps.width}
      isResizeable={true}
      minWidth={sizeProps.minWidth}
      minHeight={sizeProps.minHeight}
      menu={[
        {
          name: "File",
          list: (
            <List width="200px">
              <List.Item onClick={handleCloseModal}>Exit</List.Item>
            </List>
          ),
        },
      ]}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Frame
          padding="0px"
          boxShadow="$in"
          overflow="hidden"
          backgroundColor="#fff"
          flexGrow={1}
          fontSize="11px"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* toolbar + datatable rendering for registry entries */}
          <div
            style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
          >
            <div style={{ flex: 1 }}>
              <DataTable columns={columns} data={data} style={{ width: "100%" }} />
            </div>
          </div>
        </Frame>
      </div>
    </Modal>
  );
}
